import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { Link } from 'react-router';
import type { QueryStatus } from '@tanstack/react-query';
import PlusIcon from '@mui/icons-material/Add';
import {
  FullScreenError as PageError,
  FullScreenSpinner as PageSpinner,
} from 'components/FullScreen';
import Button from '@mui/material/Button';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import Notification from 'components/Notification';
import useHistoryState from 'components/hooks/useHistoryState';
import { useSearchContext } from 'components/SearchFilter/SearchProvider';
import { useDebounceValue } from 'components/hooks/useDebouncedValue';
import {
  getMinimumSearchCharacters,
  getStartsWithContainsFilterOptions,
  SEARCH_MODES,
  SearchMode,
} from 'api/search';
import TeamTitleCounter from 'pages/Learners/TeamsTab/TeamTitleCounter';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import type { ApiPaginationProps } from 'components/Pagination/Pagination';
import TeamsPaginatedList from 'pages/Learners/TeamsTab/TeamsList/TeamsPaginatedList';
import { useAuth } from 'contexts/AuthContext';
import usePaginatedTeams from './TeamTab.helper';
import LearnerSearchMessage from '../LearnersTab/LearnerSearchMessage';

type TeamsTabProps = {
  organisationId: string | null;
  handleOpenOrgSelector: () => void;
};

const Loader = ({ status }: { status: QueryStatus }) => {
  const { t } = useTranslation();

  if (status === 'error') {
    return (
      <PageError
        message={t('ui.event-management.learners.error-loading-teams')}
      />
    );
  }
  if (status === 'pending') {
    return <PageSpinner message={t('ui.event-management.loading')} />;
  }
  return null;
};

const TeamsTab = ({ organisationId, handleOpenOrgSelector }: TeamsTabProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLInputElement>(null);
  const createdBy = useAuth().user?.id;
  const {
    searchState: { searchTerm, checkbox: showMyTeam, filter = 'STARTS_WITH' },
    setSearchState,
  } = useSearchContext<SearchMode>(SEARCH_MODES);
  const debouncedSearchTerm = useDebounceValue(searchTerm ?? '');

  const {
    enabled,
    status,
    isFetching,
    items,
    totalCount,
    hasMore,
    loadMore,
    paginator,
  } = usePaginatedTeams({
    searchTerm: debouncedSearchTerm,
    searchMode: filter,
    myTeamsOnly: !!showMyTeam,
    organisationId: organisationId ?? '',
    createdBy,
  });
  const title = (
    <TeamTitleCounter
      isFetched={enabled && !isFetching}
      myTeamsOnly={showMyTeam}
      underMinSearchLength={
        debouncedSearchTerm.length < getMinimumSearchCharacters(filter)
      }
      items={paginator.items}
      searchTerm={debouncedSearchTerm}
      count={totalCount}
    />
  );
  const [teamNameDeletedViaNavigation] = useHistoryState<string | undefined>(
    'deletedTeam',
  );
  const [teamNameDeletedViaList, setTeamNameDeletedViaList] = useState<
    string | undefined
  >(undefined);

  const onChange = (newSearchTerm: string) => {
    setSearchState({ searchTerm: newSearchTerm });
  };

  const deletedName = teamNameDeletedViaNavigation ?? teamNameDeletedViaList;
  let notification = null;
  if (deletedName) {
    notification = t('ui.event-management.learners.teams.delete-success', {
      teamName: deletedName,
    });
  }

  const teamData = items;

  const apiPaginationProps: ApiPaginationProps = {
    hasMoreToLoad: hasMore,
    loadMore,
    loadingState: status,
    isFetching,
  };

  const minSearchCharacterLimit = getMinimumSearchCharacters(filter);

  return (
    <>
      {!organisationId &&
        (!searchTerm || searchTerm.length < minSearchCharacterLimit) && (
          <LearnerSearchMessage
            i18nKey="ui.event-management.learners.teams.search-limit-message"
            onClick={handleOpenOrgSelector}
          />
        )}
      <SearchFilter<{ leftSelectOptionsType: SearchMode }>
        labelText={`${t('ui.event-management.list-filter.search')} ${t(
          'ui.event-management.list-filter.by-name',
        )}`}
        placeholder={t('ui.event-management.learners.search.placeholder')}
        handleTermChange={onChange}
        allowEmptySearches={false}
        showButton={false}
        inputState={searchTerm}
        minCharacters={minSearchCharacterLimit}
        ref={ref}
        checkbox={{
          checked: !!showMyTeam,
          label: t('ui.event-management.learners.all-teams.my-teams-filter'),
          onChange: (value) => setSearchState({ checkbox: value }),
        }}
        leftSelectProps={{
          options: getStartsWithContainsFilterOptions(t),
          value: filter,
          onChange: (newFilter: SearchMode) => {
            setSearchState({ filter: newFilter });
          },
        }}
        isLabelAboveSearchInput
      />

      {notification && <Notification message={notification} />}
      <Loader status={status} />
      {status !== 'pending' && status !== 'error' && (
        <>
          <Box
            sx={{
              mt: (theme) => theme.spacing(spacingSizeMap.M),
              mb: (theme) => theme.spacing(spacingSizeMap.XS),
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Grid size={{ xs: 12, sm: 'auto' }}>{title}</Grid>
              <Grid size={{ xs: 3, sm: 'auto' }}>
                <Button
                  sx={{ padding: '0.5rem 1rem' }}
                  variant="contained"
                  color="primary"
                  component={Link}
                  to="/teams/create"
                  startIcon={<PlusIcon />}
                  fullWidth
                >
                  {t('ui.event-management.teams.create-team')}
                </Button>
              </Grid>
            </Grid>
          </Box>
          {paginator.items?.length > 0 ? (
            <TeamsPaginatedList
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              teamsResponse={teamData!}
              apiPaginationProps={apiPaginationProps}
              paginator={paginator}
              handleItemDelete={setTeamNameDeletedViaList}
            />
          ) : (
            enabled &&
            status === 'success' &&
            t('ui.event-management.learners.teams.list.no-results')
          )}
        </>
      )}
    </>
  );
};
export default TeamsTab;
